import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../utils/supabaseClient";
import {
  NEWS_ALL_CATEGORY,
  NEWS_CATEGORY_OPTIONS,
  getNewsCategoryColor,
  getNewsCategoryLabel,
} from "../constants/newsCategories";

const ITEMS_PER_PAGE = 10;
const VALID_NEWS_CATEGORY_VALUES = new Set([
  NEWS_ALL_CATEGORY.value,
  ...NEWS_CATEGORY_OPTIONS.map((category) => category.value),
]);

function normalizeNewsCategory(rawCategory) {
  if (!rawCategory) return NEWS_ALL_CATEGORY.value;
  return VALID_NEWS_CATEGORY_VALUES.has(rawCategory)
    ? rawCategory
    : NEWS_ALL_CATEGORY.value;
}

function getPreferredLang() {
  return localStorage.getItem("preferredLang") || "en";
}

function localizeNewsItem(item, preferredLang) {
  const useEnglish = item.is_english_only || preferredLang !== "ko";

  return {
    ...item,
    localizedTitle: useEnglish ? item.title : item.title_korean,
    localizedHtml: useEnglish ? item.html : item.html_korean,
    localizedExternalLink: useEnglish
      ? item.external_link
      : item.external_link_korean,
    localizedExternalDescription: useEnglish
      ? item.external_link_description
      : item.external_link_description_korean,
  };
}

function toPreviewText(htmlString = "") {
  const plain = htmlString.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return plain;
}

function formatDate(dateString) {
  if (!dateString) return "";
  return String(dateString).slice(0, 10);
}

function CategoryFilters({ selectedCategory, onChangeCategory }) {
  const allCategories = [NEWS_ALL_CATEGORY, ...NEWS_CATEGORY_OPTIONS];

  return (
    <div className="w-full flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-[36px] mb-[56px]">
      {allCategories.map((category) => {
        const isActive = selectedCategory === category.value;

        return (
          <button
            key={category.value}
            type="button"
            onClick={() => onChangeCategory(category.value)}
            className={`px-4 py-2 rounded-md border text-xs sm:text-sm leading-none transition-colors duration-200 ${
              isActive
                ? "bg-sky-500 border-sky-500 text-white"
                : "bg-white border-sky-500 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {category.label}
          </button>
        );
      })}
    </div>
  );
}

function NewsCard({ item, onClick }) {
  const hasCoverImage = Boolean(item.cover_image_url);
  const cardColor = getNewsCategoryColor(item.type);
  const sheerOverlaySrc = `${process.env.PUBLIC_URL}/png/bg-sheer.png`;
  const localizedExternalLink = String(item.localizedExternalLink || "").trim();
  const hasExternalLink = Boolean(localizedExternalLink);
  const externalDescription = item.localizedExternalDescription ?? "";
  const descriptionText = hasExternalLink
    ? toPreviewText(externalDescription)
    : toPreviewText(item.localizedHtml);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-full w-full flex-col overflow-hidden text-left"
    >
      {hasCoverImage && (
        <div className="relative aspect-[3/2] w-full shrink-0 overflow-hidden bg-transparent">
          <img
            src={item.cover_image_url}
            alt={item.localizedTitle}
            className="w-full h-full object-cover"
          />

          <img
            src={sheerOverlaySrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />

          <div className="absolute top-3 left-3 z-10 bg-white text-slate-800 text-[10px] sm:text-xs px-3 py-1 leading-none">
            {getNewsCategoryLabel(item.type)}
          </div>
          <div className="absolute top-3 right-3 z-10 text-white text-[10px] sm:text-xs leading-none">
            {formatDate(item.date)}
          </div>
        </div>
      )}

      <div
        className="flex h-full w-full flex-1 flex-col justify-start bg-[var(--card-color)] px-5 py-5 transition-colors duration-200 group-hover:bg-[#D1D5DB] sm:px-6 sm:py-6"
        style={{ "--card-color": cardColor }}
      >
        {!hasCoverImage && (
          <div className="mb-8 flex items-center justify-between gap-3">
            <div className="bg-white px-3 py-1 text-[10px] leading-none text-slate-800 sm:text-xs">
              {getNewsCategoryLabel(item.type)}
            </div>
            <div className="text-[10px] leading-none text-white sm:text-xs">
              {formatDate(item.date)}
            </div>
          </div>
        )}

        <div className="w-full flex items-start mb-4">
          <h3
            className="w-[80%] text-left text-white group-hover:text-black font-[650] text-xl sm:leading-[1.28] break-words line-clamp-3 transition-colors duration-200"
          >
            {item.localizedTitle}
          </h3>
        </div>
        <p
          className={`w-[90%] mb-10 text-left text-white/90 group-hover:text-slate-900 font-[350] text-sm sm:text-sm leading-5 sm:leading-7 break-words transition-colors duration-200 ${
            hasCoverImage ? "line-clamp-3" : "line-clamp-6"
          }`}
        >
          {descriptionText}
        </p>
      </div>
    </button>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pageNumbers = new Set([1, totalPages]);

  for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
    if (i > 1 && i < totalPages) {
      pageNumbers.add(i);
    }
  }

  if (currentPage <= 3) {
    pageNumbers.add(2);
    pageNumbers.add(3);
  }

  if (currentPage >= totalPages - 2) {
    pageNumbers.add(totalPages - 1);
    pageNumbers.add(totalPages - 2);
  }

  const sortedPages = Array.from(pageNumbers)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  return (
    <div className="w-full flex items-center justify-center gap-3 mt-10 text-sm text-slate-700">
      {sortedPages.map((page, index) => {
        const prevPage = sortedPages[index - 1];
        const showEllipsis = index > 0 && page - prevPage > 1;

        return (
          <React.Fragment key={page}>
            {showEllipsis && <span>...</span>}
            <button
              type="button"
              onClick={() => onPageChange(page)}
              className={`min-w-6 ${
                currentPage === page ? "font-bold text-black" : "hover:text-black"
              }`}
            >
              {page}
            </button>
          </React.Fragment>
        );
      })}

      {currentPage < totalPages && (
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          className="ml-1 hover:text-black"
        >
          Next
        </button>
      )}
    </div>
  );
}

function NewsContainers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [newsList, setNewsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const selectedCategory = normalizeNewsCategory(searchParams.get("category"));

  useEffect(() => {
    getNews();
  }, []);

  async function getNews() {
    if (!supabase) return;

    const preferredLang = getPreferredLang();
    const { data, error } = await supabase
      .from("news_advanced")
      .select("*")
      .order("date", { ascending: false })
      .order("created_at", { ascending: false });

    if (!error && data) {
      setNewsList(data.map((item) => localizeNewsItem(item, preferredLang)));
    }
  }

  const filteredNews = useMemo(() => {
    if (selectedCategory === NEWS_ALL_CATEGORY.value) {
      return newsList;
    }

    return newsList.filter((item) => item.type === selectedCategory);
  }, [newsList, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / ITEMS_PER_PAGE));

  const pagedNews = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredNews.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredNews, currentPage]);

  useEffect(() => {
    const rawCategory = searchParams.get("category");
    const normalizedCategory = normalizeNewsCategory(rawCategory);

    if (rawCategory !== normalizedCategory) {
      setSearchParams({ category: normalizedCategory }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  function handleCategoryChange(nextCategory) {
    const normalizedCategory = normalizeNewsCategory(nextCategory);
    setSearchParams({ category: normalizedCategory });
  }

  function handleCardClick(item) {
    const externalLink = String(item.localizedExternalLink || "").trim();
    const hasExternalLink = Boolean(externalLink);

    if (hasExternalLink && externalLink) {
      window.open(externalLink, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(`/company/news/${item.id}`);
  }

  return (
    <div className="flex flex-col items-center max-w-[70rem] w-full font-pretendard px-5">
      <CategoryFilters
        selectedCategory={selectedCategory}
        onChangeCategory={handleCategoryChange}
      />

      <div className="w-full grid auto-rows-fr grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {pagedNews.map((item) => (
          <NewsCard key={item.id} item={item} onClick={() => handleCardClick(item)} />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}

export default NewsContainers;
